// frontend/src/context/NotificationContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { notificationService } from '../services/notificationService';
import { useAuth } from '../hooks/useAuth';

const NotificationContext = createContext();

export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotificationContext must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    if (!user) {
      setUnreadCount(0);
      return;
    }

    try {
      const result = await notificationService.getUnreadCount();
      if (result.success) {
        setUnreadCount(result.data?.count || 0);
       
      }
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  }, [user]);

  // Fetch recent notifications
  const fetchNotifications = useCallback(async () => {
    if (!user) {
      setNotifications([]);
      return;
    }

    setLoading(true);
    try {
      const result = await notificationService.getNotifications({ 
        limit: 10,
        isRead: false 
      });
      
      if (result.success) {
        setNotifications(result.data?.notifications || []);
       
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Initial fetch when user logs in
  useEffect(() => {
    if (user) {
      
      fetchUnreadCount();
      fetchNotifications();
    } else {
      setUnreadCount(0);
      setNotifications([]);
    }
  }, [user, fetchUnreadCount, fetchNotifications]);

  
  useEffect(() => {
    if (!user) return;

    
    
    // Fetch immediately
    fetchUnreadCount();
    
    let previousCount = unreadCount;
    
    // Set up polling interval
    const interval = setInterval(async () => {
     
      
      try {
        const result = await notificationService.getUnreadCount();
        if (result.success) {
          const newCount = result.data?.count || 0;
          
          // Check if there are NEW notifications
          if (newCount > previousCount) {
           
            
            // Fetch the latest notifications
            const notifResult = await notificationService.getNotifications({ 
              limit: 5,
              isRead: false 
            });
            
            if (notifResult.success && notifResult.data?.notifications) {
              // Show toast for new notifications
              const newNotifications = notifResult.data.notifications.slice(0, newCount - previousCount);
              newNotifications.forEach(notification => {
                // Dispatch custom event for toast
                window.dispatchEvent(new CustomEvent('newNotification', {
                  detail: notification
                }));
              });
            }
          }
          
          previousCount = newCount;
          setUnreadCount(newCount);
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
      
      fetchNotifications();
    }, 30000); // 30 seconds

    return () => {
     
      clearInterval(interval);
    };
  }, [user, fetchUnreadCount, fetchNotifications, unreadCount]);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId) => {
    try {
      const result = await notificationService.markAsRead(notificationId);
      if (result.success) {
        setUnreadCount(prev => Math.max(0, prev - 1));
        setNotifications(prev =>
          prev.map(n => n._id === notificationId ? { ...n, isRead: true } : n)
        );
      }
      return result;
    } catch (error) {
      console.error('Failed to mark as read:', error);
      return { success: false };
    }
  }, []);

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    try {
      const result = await notificationService.markAllAsRead();
      if (result.success) {
        setUnreadCount(0);
        setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      }
      return result;
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      return { success: false };
    }
  }, []);

  // Delete notification
  const deleteNotification = useCallback(async (notificationId) => {
    try {
      const result = await notificationService.deleteNotification(notificationId);
      if (result.success) {
        const deletedNotif = notifications.find(n => n._id === notificationId);
        if (deletedNotif && !deletedNotif.isRead) {
          setUnreadCount(prev => Math.max(0, prev - 1));
        }
        setNotifications(prev => prev.filter(n => n._id !== notificationId));
      }
      return result;
    } catch (error) {
      console.error('Failed to delete notification:', error);
      return { success: false };
    }
  }, [notifications]);

  // Manual refresh
  const refresh = useCallback(() => {
   
    fetchUnreadCount();
    fetchNotifications();
  }, [fetchUnreadCount, fetchNotifications]);

  const value = {
    unreadCount,
    notifications,
    loading,
    fetchUnreadCount,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refresh
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;